import { useMemo, useEffect, useState } from "react";
import { selectCurrentProject } from "@features/project/projectSlice";
import { useAppSelector, useThreadsProcessor } from "@app/hooks";
import { Formik, FormikHelpers } from "formik";
import { BEModulesClassNames, FEModules } from "@utils/moduleList";
import { ModuleFull } from "./inputsTypes";
import {
	useContextAwareGetInputs,
	useUpdateInputsMutation,
	useContextAwareGetInputEntries,
	useUpdateInputEntryMutation
} from "@hooks/index";
import { getSchemas, validationSchema, initialValues } from "./inputs";
import { useGetInputTypesQuery } from "@hooks/index";
import { selectCurrentBuilder } from "@features/builder/builderSlice";
import useModuleHook from "../useModuleHook";
import useErrorMessage from "@utils/useErrorMessage";
import InputsTierOne from "./InputsTierOne";
import GenericTierTwo from "../GenericTierTwo";


const Inputs = () => {
	const { activeActivityId } = useAppSelector(selectCurrentProject);
	const { moduleList } = useAppSelector(selectCurrentBuilder);
	const id = moduleList.find(({ id }) => id === FEModules.Inputs)?.uniqueId;

	const { data: inputsData, isLoading, isError, error } = useContextAwareGetInputs(id ?? 0, { refetchOnMountOrArgChange: true });
	const { data: entriesData, isLoading: isEntriesLoading, isError: isEntriesError, error: entriesError } = useContextAwareGetInputEntries({ activityId: activeActivityId ?? 0 });
	const { data: inputTypes } = useGetInputTypesQuery();
	const [updateInputs, { isError: isUpdateError, error: updateError, }] = useUpdateInputsMutation();
	const [updateInputEntry, { isError: isUpdateInputEntryError, error: updateInputEntryError, isLoading: isUpdateInputEntryLoading }] = useUpdateInputEntryMutation();

	const { moduleSchema, t2Schema } = useMemo(() => getSchemas(), []);
	const [initValues, setInitValues] = useState<ModuleFull>(initialValues);

	const { errorMsg: updateParentErrorMsg, tabsOpen } = useModuleHook({
		skeletonsLoadingDeps: [isLoading, isEntriesLoading, isUpdateInputEntryLoading],
		isMutateError: isUpdateError,
		mutateError: updateError,
	})

	const { errorMsg: fetchError } = useErrorMessage({ isError: isEntriesError ?? isError, error: entriesError ?? error });
	const { errorMsg: updateEntryErrorMsg } = useErrorMessage({ isError: isUpdateInputEntryError, error: updateInputEntryError });
	const { processModuleThreads } = useThreadsProcessor<ModuleFull>();

	useEffect(() => {
		if (!inputsData) return
		setInitValues((cur) => ({ ...cur, parent: inputsData }))
	}, [inputsData])

	useEffect(() => {
		if (entriesData)
			processModuleThreads({ entries: entriesData })
	}, [processModuleThreads, entriesData])

	const handleSubmit = async (
		values: ModuleFull,
		{ resetForm }: FormikHelpers<ModuleFull>
	) => {
		const { entries, parent } = values
		try {
			const promises: Promise<any>[] = []
			const parentPayload = { body: { ...parent }, activityId: activeActivityId ?? 0 }

			entries.forEach((entry) => {
				const { input_type_unit, ...entryWithoutUiFields } = (entry as any)
				const entryType = inputTypes?.find((type) => type.id === entry.input_type)
				//so that if a user changes input type and the new input type doesn't have some emissions, the t2 value that was set is reset
				const curatedEntry = {
					...entryWithoutUiFields,
					co2_emissions_t2: entryType?.has_co2_emissions ? entry.co2_emissions_t2 : null,
					n2o_emissions_t2: entryType?.has_n2o_emissions ? entry.n2o_emissions_t2 : null,
					co2_e_emissions_t2: entryType?.has_co2_e_emissions ? entry.co2_e_emissions_t2 : null
				}
				const payload = { body: curatedEntry, activityId: activeActivityId ?? 0 }
				if (entry.id) promises.push(updateInputEntry(payload))
			})

			await Promise.all(promises)
			await new Promise(resolve => setTimeout(resolve, 500));
			if (parent.id) updateInputs(parentPayload)
			resetForm({ values })
		} catch (error) {
			console.error(error)
		}
	}


	return (
		<Formik
			validationSchema={validationSchema}
			initialValues={initValues}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			<>
				<InputsTierOne
					title="project.inputs"
					initValues={initValues}
					t2Schema={t2Schema}
					beModuleType={BEModulesClassNames.Inputs}
					moduleSchema={moduleSchema}
					isLoading={isLoading || isEntriesLoading || isUpdateInputEntryLoading}
					isEntriesLoading={isEntriesLoading}
					footerError={updateParentErrorMsg ?? updateEntryErrorMsg}
					blockingError={fetchError}
					setInitValues={setInitValues}
				/>
				<GenericTierTwo
					title="project.inputs"
					t2Schema={t2Schema}
					tabsOpen={tabsOpen}
				/>
			</>
		</Formik>
	);
};

export default Inputs;
